import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { OPP_STATUS, clp, fmtDate } from "@/lib/format";

export const Pick = ({ value, onChange, options, placeholder, testid, disabled }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
  </Select>
);

export const useLookups = () => {
  const [l, setL] = useState({ clients: [], users: [] });
  useEffect(() => {
    Promise.all([api.get("/gerencia/clients", { params: { status: "activo", page_size: 200 } }), api.get("/gerencia/users-lookup")])
      .then(([c, u]) => setL({ clients: c.data.items.map((x) => ({ id: x.id, name: `${x.razon_social} · ${x.rut}` })), users: u.data }));
  }, []);
  return l;
};

export const OppForm = ({ value, onChange, lookups }) => {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    if (value.client_id) api.get(`/gerencia/clients/${value.client_id}`).then((r) => setContacts(r.data.contacts.filter((c) => c.active).map((c) => ({ id: c.id, name: c.nombre }))));
  }, [value.client_id]);
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2"><Field label="Título / descripción corta *"><Input data-testid="opp-title-input" value={value.title} onChange={(e) => set("title")(e.target.value)} /></Field></div>
      <Field label="Cliente *"><Pick testid="opp-client-select" value={value.client_id} onChange={(v) => onChange({ ...value, client_id: v, contact_id: null })} options={lookups.clients} placeholder="Seleccione cliente" /></Field>
      <Field label="Contacto"><Pick testid="opp-contact-select" value={value.contact_id} onChange={set("contact_id")} options={contacts} placeholder="Seleccione contacto" /></Field>
      <Field label="Responsable comercial *"><Pick testid="opp-owner-select" value={value.sales_owner_id} onChange={set("sales_owner_id")} options={lookups.users} placeholder="Seleccione" /></Field>
      <Field label="Fecha *"><Input data-testid="opp-date-input" type="date" value={value.date} onChange={(e) => set("date")(e.target.value)} /></Field>
      <Field label="Fecha estimada de decisión"><Input data-testid="opp-decision-date-input" type="date" value={value.est_decision_date || ""} onChange={(e) => set("est_decision_date")(e.target.value || null)} /></Field>
      <div className="sm:col-span-2"><Field label="Descripción"><Textarea data-testid="opp-description-input" value={value.description} onChange={(e) => set("description")(e.target.value)} /></Field></div>
      <div className="sm:col-span-2"><Field label="Observaciones"><Textarea data-testid="opp-notes-input" value={value.notes} onChange={(e) => set("notes")(e.target.value)} /></Field></div>
    </div>
  );
};

export const emptyOpp = () => ({ title: "", client_id: "", contact_id: null, sales_owner_id: "", date: new Date().toISOString().slice(0, 10), est_decision_date: null, description: "", notes: "" });

export default function Sales() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20, by_status: {} });
  const [open, setOpen] = useState(false);
  const lookups = useLookups();
  const [form, setForm] = useState({ ...emptyOpp(), sales_owner_id: user.id });
  useEffect(() => { api.get("/gerencia/opportunities", { params: { q, status, page, client_id: params.get("client_id") || "" } }).then((r) => setData(r.data)); }, [q, status, page, params]);

  const create = async (e) => {
    e.preventDefault();
    try { const { data: o } = await api.post("/gerencia/opportunities", form); toast.success(`Venta ${o.code} creada`); navigate(`/gerencia/ventas/${o.id}`); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="sales-page">
      <PageHeader eyebrow="Gerencia" title="Ventas" subtitle="Cliente → Oportunidad → Cotización → Seguimiento → Adjudicación → OT."
        actions={can("gerencia.ventas.crear") && <Button data-testid="create-sale-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nueva venta</Button>} />
      <div className="flex gap-2 overflow-x-auto pb-3 mb-3" data-testid="sales-status-pills">
        <button onClick={() => { setParams({}); setPage(1); }} data-testid="sales-pill-all" className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold ${!status ? "bg-[#258F00] text-white border-[#258F00]" : "bg-white border-[#e4e8e1]"}`}>Todas</button>
        {Object.entries(OPP_STATUS).map(([k, v]) => (
          <button key={k} data-testid={`sales-pill-${k}`} onClick={() => { setParams({ status: k }); setPage(1); }}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold ${status === k ? "bg-[#258F00] text-white border-[#258F00]" : "bg-white border-[#e4e8e1]"}`}>
            {v.label} <span className="opacity-70">{data.by_status[k]?.count ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="syc-card overflow-hidden">
        <div className="p-4 border-b border-[#e4e8e1] relative">
          <Search size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-[#8a9584]" />
          <Input data-testid="sales-search-input" className="pl-9" placeholder="Buscar por título o número" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        {data.items.length === 0 ? <EmptyState title="Sin ventas" /> : data.items.map((o) => (
          <Link key={o.id} to={`/gerencia/ventas/${o.id}`} data-testid={`sale-row-${o.id}`} className="grid grid-cols-[1fr_auto] md:grid-cols-[130px_1fr_200px_140px_130px] items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm">
            <span className="font-mono text-xs hidden md:block">{o.code}</span>
            <div className="min-w-0"><div className="font-semibold truncate">{o.title}</div><div className="text-xs text-[#5b6656] truncate">{o.client_name} · {fmtDate(o.date)}</div></div>
            <span className="text-xs text-[#5b6656] hidden md:block truncate">{o.sales_owner_name}</span>
            <span className="font-semibold tabular-nums hidden md:block">{clp(o.total)}</span>
            <StatusBadge status={OPP_STATUS[o.status].tone} label={OPP_STATUS[o.status].label} />
          </Link>
        ))}
        <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-sale-dialog">
          <DialogHeader><DialogTitle>Nueva venta / oportunidad</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <OppForm value={form} onChange={setForm} lookups={lookups} />
            <DialogFooter><Button data-testid="create-sale-submit-btn" className="rounded-full">Crear venta</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
